import { createEntityAdapter, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../rootReducer'
import { ReceivedChatMessage } from '@livekit/components-core'
import { IMessage } from '@/src/types/chat.type'

export interface IChatState {
  prevMessages: IMessage[]
  messages: IMessage[]
  localParticipantId: string
  isOpen: boolean
}

export const initialChatState: IChatState = {
  prevMessages: [],
  messages: [],
  localParticipantId: '',
  isOpen: false,
}

const chatAdapter = createEntityAdapter()

export const chatSlice = createSlice({
  name: 'chat',
  initialState: chatAdapter.getInitialState(initialChatState),
  reducers: {
    setChatState: (state, action: PayloadAction<IChatState>) => ({
      ...state,
      ...action.payload,
    }),
    setChatMessages: (state, action: PayloadAction<IMessage[]>) => {
      let hasMineMsg: boolean = false

      // reduceRight нужен для того, чтобы идти с конца сообщений чата. Это нужно для того, чтобы если мы встретим снизу свои сообщения, тогда все верхнии сообщения уже должны быть прочитаны
      state.messages = action.payload
        ?.reduceRight((acc, message) => {
          if (!hasMineMsg && message?.author?.id === state.localParticipantId) {
            hasMineMsg = true
          }

          const customMsg: IMessage = {
            ...message,
            isViewed:
              hasMineMsg ||
              state.isOpen ||
              state.prevMessages?.find((msg) => msg?.id === message?.id)?.isViewed,
          }

          return [...acc, customMsg]
        }, [])
        ?.toReversed()
      state.prevMessages = state?.messages
    },
    setLocalParticipantId: (state, action: PayloadAction<string>) => {
      state.localParticipantId = action.payload
    },
    addChatMessage: (state, action: PayloadAction<IMessage>) => {
      const msg: IMessage = {
        ...action.payload,
        isViewed: state.isOpen || action.payload.author?.id === state.localParticipantId,
      }
      state.messages = [...state.messages, msg]
      state.prevMessages = state.messages
    },
    setIsOpenChat: (state, action: PayloadAction<boolean>) => {
      state.isOpen = action.payload

      if (action.payload) {
        state.messages = [
          ...state.messages?.map((message) => ({
            ...message,
            isViewed: true,
          })),
        ]
        state.prevMessages = state.messages
      }
    },
  },
})

export const { setChatState, setChatMessages, addChatMessage, setLocalParticipantId, setIsOpenChat } =
  chatSlice.actions

export const { selectAll: chatSelector } = chatAdapter.getSelectors(
  (state: RootState) => state[chatSlice.name],
)

export const selectChatState = (state: RootState) => state.chat

export const selectChatMessages = (state: RootState) => state.chat.messages
export const selectUnreadMessages = (state: RootState) =>
  state.chat.messages?.filter((message) => !message?.isViewed)?.length

export const selectLocalParticipantId = (state: RootState) => state.chat.localParticipantId
export const selectIsOpenChat = (state: RootState) => state.chat.isOpen
